import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  useTranslation,
  useAuthSession,
  type SupabaseClient,
  type StorefrontSocialProvider,
} from '@dukanti/shared';
import {
  STOREFRONT_FOOTER_SOCIAL_LINK_LIMIT,
  sanitizeStoreSocialProfiles,
  storeSocialProfilesAdminKeys,
  storeSocialProfilesErrorKey,
  useStoreSocialProfiles,
  useUpdateStoreSocialProfiles,
  type StoreSocialProfilesErrorKey,
} from '@dukanti/shared/admin';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useNavBlocker } from '@/hooks/use-nav-blocker';
import { NativeSelect } from '@/features/website-templates/components/native-select';

interface ProfileDraft {
  readonly id: string;
  readonly provider: StorefrontSocialProvider;
  readonly url: string;
}

interface SettingsDraft {
  readonly identity: object;
  readonly storeId: string;
  readonly client: SupabaseClient;
  readonly updatedAt: string;
  readonly initial: readonly ProfileDraft[];
  readonly profiles: readonly ProfileDraft[];
}

export interface SocialProfilesFormProps {
  readonly client: SupabaseClient;
  readonly storeId: string;
  readonly enabled?: boolean;
  readonly permissionsLoading?: boolean;
}

export function SocialProfilesForm({
  client,
  storeId,
  enabled = true,
  permissionsLoading = false,
}: SocialProfilesFormProps): React.JSX.Element {
  const { t, dir } = useTranslation();
  const queryClient = useQueryClient();
  const session = useAuthSession(client);
  const [draft, setDraft] = useState<SettingsDraft | null>(null);
  const [error, setError] = useState<StoreSocialProfilesErrorKey | null>(null);
  const [denied, setDenied] = useState(false);
  const [reloading, setReloading] = useState(false);
  const saving = useRef(false);
  const userId = session.user?.id ?? null;
  const nativeSession = session.session;
  const identity = useMemo(
    () => ({ client, storeId, enabled, userId, nativeSession }),
    [client, storeId, enabled, userId, nativeSession],
  );
  const [readyIdentity, setReadyIdentity] = useState<object | null>(null);
  const authorized =
    enabled && !denied && !session.isLoading && userId !== null && readyIdentity === identity;
  const live = useRef({ client, storeId, active: true, enabled, identity });
  live.current = { client, storeId, active: true, enabled: authorized, identity };
  const settings = useStoreSocialProfiles(client, { storeId, enabled: authorized });
  const update = useUpdateStoreSocialProfiles(client);
  const queryDenied =
    storeSocialProfilesErrorKey(settings.failureReason ?? settings.error, 'load') ===
    'admin.storeSettings.socialProfiles.forbidden';
  const current = draft?.identity === identity && authorized && !queryDenied ? draft : null;
  const isDirty = Boolean(
    current && JSON.stringify(current.profiles) !== JSON.stringify(current.initial),
  );
  useNavBlocker(isDirty && enabled && !denied);

  useEffect(() => {
    live.current.active = true;
    setDraft(null);
    setError(null);
    setDenied(false);
    setReloading(false);
    saving.current = false;
    const queryKey = storeSocialProfilesAdminKeys.settings(client, storeId);
    void queryClient.cancelQueries({ queryKey, exact: true });
    queryClient.removeQueries({ queryKey, exact: true });
    setReadyIdentity(identity);
    return () => {
      if (live.current.client === client && live.current.storeId === storeId)
        live.current.active = false;
      const queryKey = storeSocialProfilesAdminKeys.settings(client, storeId);
      void queryClient.cancelQueries({ queryKey, exact: true });
      queryClient.removeQueries({ queryKey, exact: true });
    };
  }, [client, storeId, queryClient, identity]);

  useEffect(() => {
    if (!enabled || denied) {
      setDraft(null);
      setReloading(false);
      const queryKey = storeSocialProfilesAdminKeys.settings(client, storeId);
      void queryClient.cancelQueries({ queryKey, exact: true });
      queryClient.removeQueries({ queryKey, exact: true });
    } else if (authorized && settings.data && !settings.isError) {
      const data = settings.data;
      setDraft((previous) =>
        previous?.identity === identity
          ? previous
          : {
              client,
              identity,
              storeId,
              updatedAt: data.updatedAt,
              initial: data.profiles,
              profiles: data.profiles,
            },
      );
    }
  }, [
    client,
    storeId,
    enabled,
    denied,
    authorized,
    identity,
    settings.data,
    settings.isError,
    queryClient,
  ]);

  useEffect(() => {
    if (authorized && queryDenied) {
      setDenied(true);
      setDraft(null);
    }
  }, [authorized, queryDenied]);

  const scopeIsCurrent = (origin: typeof live.current): boolean =>
    live.current.active &&
    live.current.enabled &&
    live.current.identity === origin.identity &&
    live.current.client === origin.client &&
    live.current.storeId === origin.storeId;

  async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (
      !current ||
      !enabled ||
      denied ||
      !isDirty ||
      saving.current ||
      update.isPending ||
      reloading ||
      error === 'admin.storeSettings.socialProfiles.stale'
    )
      return;
    const profiles = sanitizeStoreSocialProfiles(current.profiles);
    if (!profiles) {
      setError('admin.storeSettings.socialProfiles.invalid');
      return;
    }
    const origin = { ...live.current };
    saving.current = true;
    setError(null);
    try {
      const result = await update.mutateAsync({
        storeId,
        profiles,
        expectedUpdatedAt: current.updatedAt,
      });
      if (!scopeIsCurrent(origin)) return;
      setDraft({
        identity,
        client,
        storeId,
        updatedAt: result.updatedAt,
        profiles: result.profiles,
        initial: result.profiles,
      });
      toast.success(t('admin.storeSettings.socialProfiles.saved'));
    } catch (failure) {
      if (!scopeIsCurrent(origin)) return;
      const key = storeSocialProfilesErrorKey(failure);
      if (key === 'admin.storeSettings.socialProfiles.forbidden') {
        setDenied(true);
        setDraft(null);
      }
      setError(key);
    } finally {
      if (scopeIsCurrent(origin)) saving.current = false;
    }
  }

  async function reload(): Promise<void> {
    if (update.isPending || reloading || !enabled || denied) return;
    const origin = { ...live.current };
    setReloading(true);
    try {
      const result = await settings.refetch();
      if (!scopeIsCurrent(origin)) return;
      if (result.error || !result.data) throw result.error ?? new Error('missing social settings');
      setDraft({
        identity,
        client,
        storeId,
        updatedAt: result.data.updatedAt,
        profiles: result.data.profiles,
        initial: result.data.profiles,
      });
      setError(null);
    } catch (failure) {
      if (!scopeIsCurrent(origin)) return;
      const key = storeSocialProfilesErrorKey(failure, 'load');
      if (key === 'admin.storeSettings.socialProfiles.forbidden') {
        setDenied(true);
        setDraft(null);
      }
      setError(key);
    } finally {
      if (scopeIsCurrent(origin)) setReloading(false);
    }
  }

  function change(profiles: readonly ProfileDraft[]): void {
    if (!current) return;
    setDraft({ ...current, profiles });
    setError((previous) =>
      previous === 'admin.storeSettings.socialProfiles.stale' ? previous : null,
    );
  }

  const forbidden = !permissionsLoading && (!enabled || denied || queryDenied);
  const loadError = settings.isError ? storeSocialProfilesErrorKey(settings.error, 'load') : null;
  return (
    <form
      noValidate
      dir={dir}
      onSubmit={(event) => void save(event)}
      className="mt-6"
      data-testid="social-profiles-form"
    >
      <section className="bg-card border-border shadow-admin-card rounded-xl border p-6 sm:p-8">
        <h2 className="font-display text-foreground text-start text-xl font-bold">
          {t('admin.storeSettings.socialProfiles.title')}
        </h2>
        <p className="text-muted-foreground mt-2 text-start text-sm">
          {t('admin.storeSettings.socialProfiles.help')}
        </p>
        {forbidden ? (
          <p role="alert" className="text-destructive mt-4">
            {t('admin.storeSettings.socialProfiles.forbidden')}
          </p>
        ) : permissionsLoading || !authorized || (!current && settings.isLoading) ? (
          <p aria-busy="true" className="text-muted-foreground mt-4">
            {t('common.loading')}
          </p>
        ) : !current ? (
          <div role="alert" className="mt-4 space-y-3">
            <p>{t(loadError ?? 'admin.storeSettings.socialProfiles.loadError')}</p>
            <Button
              type="button"
              variant="outline"
              disabled={reloading}
              onClick={() => void reload()}
            >
              {t('common.retry')}
            </Button>
          </div>
        ) : (
          <>
            <fieldset
              disabled={update.isPending || reloading}
              aria-busy={update.isPending || reloading}
              className="mt-6 space-y-4"
            >
              {current.profiles.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  {t('admin.storeSettings.socialProfiles.empty')}
                </p>
              ) : null}
              {current.profiles.map((profile) => (
                <div
                  key={profile.id}
                  className="border-border space-y-3 rounded-xl border p-4"
                  data-testid="social-profile-row"
                >
                  <div className="space-y-1.5">
                    <Label htmlFor={`social-provider-${profile.id}`}>
                      {t('admin.storeSettings.socialProfiles.provider')}
                    </Label>
                    <NativeSelect
                      id={`social-provider-${profile.id}`}
                      value={profile.provider}
                      options={(['instagram', 'facebook'] as const).map((provider) => ({
                        value: provider,
                        label: t(`admin.storeSettings.socialProfiles.${provider}`),
                      }))}
                      onChange={(value) => {
                        if (value === 'instagram' || value === 'facebook')
                          change(
                            current.profiles.map((entry) =>
                              entry.id === profile.id ? { ...entry, provider: value } : entry,
                            ),
                          );
                      }}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`social-url-${profile.id}`}>
                      {t('admin.storeSettings.socialProfiles.url')}
                    </Label>
                    <Input
                      id={`social-url-${profile.id}`}
                      type="url"
                      dir="ltr"
                      value={profile.url}
                      onChange={(event) =>
                        change(
                          current.profiles.map((entry) =>
                            entry.id === profile.id ? { ...entry, url: event.target.value } : entry,
                          ),
                        )
                      }
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() =>
                      change(current.profiles.filter((entry) => entry.id !== profile.id))
                    }
                  >
                    {t('admin.storeSettings.socialProfiles.remove')}
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                disabled={current.profiles.length >= STOREFRONT_FOOTER_SOCIAL_LINK_LIMIT}
                onClick={() =>
                  change([
                    ...current.profiles,
                    { id: crypto.randomUUID(), provider: 'instagram', url: '' },
                  ])
                }
              >
                {t('admin.storeSettings.socialProfiles.add')}
              </Button>
              <p className="text-muted-foreground text-xs">
                {t('admin.storeSettings.socialProfiles.max')}
              </p>
            </fieldset>
            {error ? (
              <p role="alert" className="text-destructive mt-4 text-sm">
                {t(error)}
              </p>
            ) : null}
            <div className="mt-5 flex flex-wrap justify-end gap-2">
              {error === 'admin.storeSettings.socialProfiles.stale' ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={reloading || update.isPending}
                  onClick={() => void reload()}
                >
                  {t('admin.storeSettings.socialProfiles.reload')}
                </Button>
              ) : null}
              {isDirty ? (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={update.isPending || reloading}
                  onClick={() => {
                    change(current.initial);
                  }}
                >
                  {t('common.cancel')}
                </Button>
              ) : null}
              <Button
                type="submit"
                disabled={
                  !isDirty ||
                  update.isPending ||
                  reloading ||
                  error === 'admin.storeSettings.socialProfiles.stale'
                }
              >
                {t(update.isPending ? 'common.loading' : 'common.save')}
              </Button>
            </div>
          </>
        )}
      </section>
    </form>
  );
}
